/**
 * Page Visit Tracker Hook
 *
 * Tracks first-time page visits for onboarding hints and guidance.
 * Loads page visit state from database on mount and provides methods to check and mark visits.
 *
 * Requirements: 13.1, 13.2, 13.3, 13.4, 13.5
 */

import { useEffect, useState } from 'react'

import initFirebase from '../db/initFirebase'
import OnboardingService from '../services/OnboardingService'

/**
 * Hook interface for tracking page visits
 */
interface UsePageVisitTracker {
  hasVisited: boolean
  markVisited: () => Promise<void>
  isLoading: boolean
}

/**
 * Custom hook that tracks first-time page visits for onboarding hints.
 *
 * This hook loads the user's page visit state from the database on mount,
 * provides a synchronous check for whether a page has been visited,
 * and persists page visits to the database while updating local state immediately.
 *
 * Usage:
 * ```tsx
 * function CalendarPage() {
 *   const { hasVisited, markVisited, isLoading} = usePageVisitTracker('calendar')
 *
 *   useEffect(() => {
 *     if (!isLoading && !hasVisited) {
 *       // Show first-time hint
 *     }
 *   }, [isLoading, hasVisited])
 *
 *   const handleDismissHint = async () => {
 *     await markVisited()
 *   }
 * }
 * ```
 *
 * Requirements:
 * - 13.1: Track which pages a user has visited for the first time
 * - 13.2: Store flag for calendar page first visit
 * - 13.3: Store flag for booking page first visit
 * - 13.4: Update User_State to record the visit
 * - 13.5: Persist page visit tracking across user sessions
 *
 * @param page - The page to track ('calendar' or 'booking')
 * @returns Object with hasVisited check, markVisited method, and loading state
 */
export const usePageVisitTracker = (
  page: 'calendar' | 'booking'
): UsePageVisitTracker => {
  const localStorageKey = `pageVisited_${page}`

  // Check localStorage first for instant, reliable "shown once" behavior
  const [hasVisited, setHasVisited] = useState(() => {
    return localStorage.getItem(localStorageKey) === 'true'
  })
  const [isLoading, setIsLoading] = useState(() => {
    // If localStorage already says visited, no need to load from DB
    return localStorage.getItem(localStorageKey) !== 'true'
  })
  const [userId, setUserId] = useState<string | null>(null)

  // Load from database only if localStorage doesn't already confirm the visit
  useEffect(() => {
    // If already marked as visited in localStorage, skip DB check entirely
    if (localStorage.getItem(localStorageKey) === 'true') {
      setHasVisited(true)
      setIsLoading(false)
      return
    }

    const loadPageVisitState = async () => {
      try {
        setIsLoading(true)

        // Get current user from Firebase Auth
        const firebase = initFirebase.getFireBaseStore()
        const auth = firebase.auth
        const currentUser = auth?.currentUser

        if (!currentUser) {
          setIsLoading(false)
          return
        }

        setUserId(currentUser.uid)
        const onboarding = await OnboardingService.getUserOnboarding(
          currentUser.uid
        )

        if (onboarding && onboarding.hasVisitedPages[page]) {
          // Sync localStorage with DB state so future visits are instant
          localStorage.setItem(localStorageKey, 'true')
          setHasVisited(true)
        }
      } catch (error) {
        console.error('Failed to load page visit state:', error)
        // On error, default to "visited" to prevent showing guidance repeatedly
        // The guidance should only ever show once - better to skip than repeat
        localStorage.setItem(localStorageKey, 'true')
        setHasVisited(true)
      } finally {
        setIsLoading(false)
      }
    }

    loadPageVisitState()
  }, [page, localStorageKey])

  /**
   * Mark a page as visited and persist to database
   *
   * Updates local state immediately for responsive UI, then persists to database.
   *
   * Requirements:
   * - 13.4: Update User_State to record the visit
   * - 13.5: Persist page visit tracking across user sessions
   */
  const markVisited = async (): Promise<void> => {
    // Always persist to localStorage first - this is the primary "show once" gate
    localStorage.setItem(localStorageKey, 'true')

    // Update local state immediately for responsive UI
    setHasVisited(true)

    if (!userId) {
      return
    }

    // Persist to database as secondary storage
    try {
      await OnboardingService.markPageVisited(userId, page)
    } catch (error) {
      console.error(`Failed to mark ${page} page as visited:`, error)
      // Don't revert local state - localStorage ensures it won't show again
    }
  }

  return {
    hasVisited,
    markVisited,
    isLoading
  }
}
